import { format as d3Format, formatLocale, min, precisionFixed } from 'd3';
import { isNil } from 'lodash';

import { type Format, FormatType } from '../types';

const convertGigaToBillion = (
  formatter: (value: number) => string
): ((value: number) => string) => {
  return (value) => formatter(value).replace('G', 'B');
};

const DefaultPrecision = 1;
const MaxPrecision = 2;

type PrecisionConfigProps = {
  values?: number[];
  increasePrecision?: number;
  currencyCode?: string;
};

export const getCurrencySymbol = (
  currencyCode?: string | null,
  locale = 'en-US'
) => {
  if (isNil(currencyCode) || currencyCode === 'USD') return '$';
  return (
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value ?? '$'
  );
};

export const getLocaleConfig = (currencySymbol: string) => {
  return formatLocale({
    decimal: '.',
    thousands: ',',
    grouping: [3],
    currency: [currencySymbol, ''],
  });
};

export const currencyFormatter = (
  formatString: string,
  currencyCode?: string | null
) => {
  const currencySymbol = getCurrencySymbol(currencyCode);
  const locale = getLocaleConfig(currencySymbol);
  return locale.format(formatString);
};

export const formatterWithExchangeRate = (
  currencyCode?: string | null,
  exchangeRate = 1
) => {
  return (value: number) => {
    if (typeof value !== 'number') return value;
    return currencyFormatter('$.2s', currencyCode)(value * exchangeRate);
  };
};

const siFormat = (num: number) => {
  const digits = Math.round(num).toString().length % 3;
  const significantDigits = (() => {
    if (digits === 0) return 3;
    return 2;
  })();

  return d3Format(`.${significantDigits}~s`);
};

export const DECIMAL_PRECISION_REGEX = /^\.\d+f$/;
export const PERCENTILE_DECIMAL_REGEX = /^\.\d+%$/;

const percentagePointFormatter = (value: number): string => {
  const scaled = value * 100;
  const formatted = d3Format('.1~f')(Math.abs(scaled));
  const sign = value < 0 ? '\u2212' : '+';
  return `${sign}${formatted} pp`;
};

export const getFormatter = (
  format: Format | string,
  { values, increasePrecision = 0, currencyCode }: PrecisionConfigProps = {}
): ((value: number) => string) => {
  if (typeof format === 'function') return format;

  if (typeof format === 'string' && PERCENTILE_DECIMAL_REGEX.test(format)) {
    return (value: number) => d3Format(format)(value);
  }

  if (typeof format === 'string' && DECIMAL_PRECISION_REGEX.test(format)) {
    const d3Spec = ',' + format;
    return (value: number) => {
      if (Math.abs(value) < 0.01 && Math.abs(value) > 0) {
        return d3Format('.2e')(value);
      }
      return d3Format(d3Spec)(value);
    };
  }

  switch (format) {
    case FormatType.DAY:
      return (value: number) => {
        const formattedValue = d3Format(',.0f')(value);
        return `${formattedValue} day${
          // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
          parseInt(formattedValue) === 1 ? '' : 's'
        }`;
      };

    case FormatType.SI:
      return (value: number) =>
        Math.abs(value) < 1000
          ? d3Format(',.0f')(value)
          : convertGigaToBillion(siFormat(value))(value);

    case FormatType.PERCENTAGE: {
      const minStep = values
        ? min(
            values.slice(1).map((val, i) => Math.abs(val - (values?.[i] ?? 0)))
          )
        : undefined;

      let precision = DefaultPrecision;

      if (minStep !== undefined) {
        const scaledMinStep = minStep * 100;
        precision = Math.max(
          DefaultPrecision,
          precisionFixed(scaledMinStep) - DefaultPrecision
        );
      }

      precision += increasePrecision;
      precision = Math.min(MaxPrecision, precision);

      return increasePrecision
        ? d3Format(`.${precision}%`)
        : d3Format(`.${precision}~%`);
    }
    case FormatType.CURRENCY:
      return (value: number) => {
        const formatter =
          value >= 1000
            ? convertGigaToBillion(currencyFormatter('$.3~s', currencyCode))
            : currencyFormatter('$,d', currencyCode);
        return formatter(value);
      };
    case FormatType.CURRENCY_INTEGER:
      return (value: number) => {
        return convertGigaToBillion(currencyFormatter('$,.0f', currencyCode))(
          value
        );
      };
    case FormatType.INTEGER:
      return d3Format(',.0f');
    case FormatType.YEAR:
      return (d) => `${d3Format('.1~f')(d)}yr`;
    case FormatType.CALENDAR_YEAR:
      return d3Format('.0f');
    case FormatType.DECIMAL: {
      return (value: number) => {
        if (Math.abs(value) < 0.01 && Math.abs(value) > 0) {
          return d3Format('.2e')(value);
        }
        return d3Format(',.2f')(value);
      };
    }
    case FormatType.PERCENTAGE_POINT:
    case 'p.p.':
      return percentagePointFormatter;
    case FormatType.SCIENTIFIC:
      return d3Format('.2e');

    default:
      return d3Format(',.0f');
  }
};
